import React from "react";
import { RowColors, RowOptions } from "../TableSection/TableSection";
import "./CardTable.scss";

interface CardTableProps {
  header: string[];
  rows: RowOptions[];
  /** Column holding the row's name. It titles the card on small screens. */
  titleIndex?: number;
  /** First column whose cells are links out to course material. */
  resourceStartIndex: number;
  blurb?: string;
  /** Wording for the title cell's own link in the small-screen list. */
  titleLinkLabel?: string;
  /** Decorative art revealed on hover, keyed by row title. */
  iconFor?: (title: string) => string | undefined;
  /** Anchor id for a specific row, used by the scroll-driven background. */
  idFor?: (title: string) => string | undefined;
  /** Tighter rows and smaller titles, used by the lectures table. */
  compact?: boolean;
  /** Optional grid-template-columns override. */
  columnTracks?: string;
  /** Reserve two lines of text so wrapping labels don’t make some rows taller. */
  twoLine?: boolean;
}

interface CardTableState {
  expandedRow: number | null;
}

const PENDING_LABEL = "Not released yet";

function isBlankCell(value: string) {
  return value === "-" || value === "—";
}

function isPendingResource(
  row: RowOptions,
  index: number,
  value: string,
  href: string | undefined,
  resourceStartIndex: number
) {
  return (
    !row.locked &&
    index >= resourceStartIndex &&
    !isBlankCell(value) &&
    !href
  );
}

function PendingHint({
  children,
  value,
  className,
}: {
  children: React.ReactNode;
  value: string;
  className?: string;
}) {
  return (
    <span
      aria-label={`${value}, ${PENDING_LABEL}`}
      className={["card-cell-pending", className].filter(Boolean).join(" ")}
    >
      {children}
      <span aria-hidden="true" className="card-cell-tip">
        {PENDING_LABEL}
      </span>
    </span>
  );
}

/** Renders course schedule data as a row of cards that collapse into an accordion. */
export class CardTable extends React.Component<CardTableProps, CardTableState> {
  state: CardTableState = { expandedRow: null };

  toggleRow = (index: number) => {
    this.setState(({ expandedRow }) => ({
      expandedRow: expandedRow === index ? null : index,
    }));
  };

  render() {
    const {
      blurb,
      compact,
      header,
      iconFor,
      idFor,
      columnTracks,
      resourceStartIndex,
      rows,
      titleLinkLabel,
      twoLine,
    } = this.props;
    const titleIndex = this.props.titleIndex ?? 0;
    const columns =
      columnTracks ??
      header
        .map((_, index) => {
          if (index === titleIndex) {
            return "minmax(6.5rem, 2.2fr)";
          }
          return index < resourceStartIndex
            ? "minmax(3rem, 0.8fr)"
            : "minmax(3.25rem, 0.95fr)";
        })
        .join(" ");

    const allLocked = rows.length > 0 && rows.every((row) => row.locked);

    return (
      <div
        className={[
          "card-table",
          compact ? "card-table--compact" : "",
          twoLine ? "card-table--two-line" : "",
          allLocked ? "is-all-locked" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ "--card-table-columns": columns } as React.CSSProperties}
      >
        {blurb && (
          <div className="card-table-intro">
            <div className="description">
              <p>{blurb}</p>
            </div>
          </div>
        )}
        <div className="card-table-headings" aria-hidden="true">
          {header.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
        {rows.map((row, rowIndex) => {
          const title = row.cellNames[titleIndex];
          const titleHref = row.cellNametoLinks.get(title);
          const isExpanded = this.state.expandedRow === rowIndex;
          const detailsId = `card-details-${rowIndex}-${title
            .toLowerCase()
            .replace(/\W+/g, "-")}`;
          const icon = iconFor?.(title);
          const details = header
            .map((label, index) => ({
              label,
              value: row.cellNames[index] ?? "-",
              href: row.cellNametoLinks.get(row.cellNames[index]),
              index,
            }))
            .filter(({ index }) => index !== titleIndex);
          const facts = details.filter(({ index }) => index < resourceStartIndex);
          const resources = details.filter(({ href, index, value }) => {
            if (index < resourceStartIndex || value === "-") {
              return false;
            }
            return Boolean(href) || href === undefined;
          });

          return (
            <article
              aria-current={row.current ? "true" : undefined}
              className={`card-row ${isExpanded ? "is-expanded" : ""} ${
                row.locked ? "is-locked" : ""
              } ${row.current ? "is-current" : ""}`}
              id={idFor?.(title)}
              key={`${title}-${rowIndex}`}
              style={{ backgroundColor: row.backgroundColor ?? RowColors.RELEASED }}
            >
              {icon && (
                <img aria-hidden="true" alt="" className="card-row-icon" src={icon} />
              )}
              {header.map((label, index) => {
                const value = row.cellNames[index] ?? "-";
                const href = row.cellNametoLinks.get(value);
                const isTitle = index === titleIndex;
                const pending = isPendingResource(
                  row,
                  index,
                  value,
                  href,
                  resourceStartIndex
                );
                const classNames = [
                  "card-cell",
                  isTitle ? "card-cell--title" : "",
                  index === header.length - 1 ? "card-cell--last" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const labelText = (
                  <span className="card-cell-text">{value}</span>
                );
                const cellInner = href ? (
                  <a href={href}>{labelText}</a>
                ) : pending ? (
                  <PendingHint value={value}>{labelText}</PendingHint>
                ) : (
                  labelText
                );

                return (
                  <div className={classNames} key={`${label}-${index}`}>
                    {isTitle && row.current && (
                      <span className="card-row-dots card-row-dots--start" aria-hidden="true" />
                    )}
                    {cellInner}
                    {isTitle && row.current && (
                      <span className="card-row-dots card-row-dots--end" aria-hidden="true" />
                    )}
                  </div>
                );
              })}
              <button
                aria-controls={detailsId}
                aria-expanded={isExpanded}
                className="card-row-toggle"
                onClick={() => this.toggleRow(rowIndex)}
                type="button"
              >
                {row.current && (
                  <span className="card-row-dots card-row-dots--start" aria-hidden="true" />
                )}
                <span className="card-cell-text">{title}</span>
                {row.current && (
                  <span className="card-row-dots card-row-dots--end" aria-hidden="true" />
                )}
                <span aria-hidden="true" className="card-row-disclosure">
                  ›
                </span>
              </button>
              <div className="card-row-details" id={detailsId}>
                {facts.length > 0 && (
                  <div className="card-row-facts">
                    {facts.map(({ label, value }) => (
                      <div className="card-row-fact" key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                )}
                <div className="card-row-links">
                  {titleHref && (
                    <a className="card-row-link" href={titleHref}>
                      {titleLinkLabel ?? title}
                    </a>
                  )}
                  {resources.map(({ label, value, href, index }) =>
                    href ? (
                      <a className="card-row-link" href={href} key={label}>
                        {value}
                      </a>
                    ) : isPendingResource(
                        row,
                        index,
                        value,
                        href,
                        resourceStartIndex
                      ) ? (
                      <PendingHint
                        className="card-row-link is-static"
                        key={label}
                        value={value}
                      >
                        {value}
                      </PendingHint>
                    ) : (
                      <span className="card-row-link is-static" key={label}>
                        {value}
                      </span>
                    )
                  )}
                  {!titleHref && resources.length === 0 && (
                    <p className="card-row-empty">Not posted yet</p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    );
  }
}
